import React from 'react'
import { Stack, Tooltip, Typography, styled } from '@mui/material'
import { IOlexMatchPreview } from 'src/generated-types/olex-match'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { ListRow, PanelCard, PanelHeader, SectionLabel } from './ExpandGrid.style'
import { OLEX_MATCH_TYPES, shortNames } from './olexMatch.action'

interface OlexMatchSummaryProps {
  preview: IOlexMatchPreview
  t: (key: string, options?: object) => string
}

// One line per position type that gains or loses positions; the names are in the tooltip.
const OlexMatchSummary: React.FC<OlexMatchSummaryProps> = ({ preview, t }) => {
  const names = (list: string[]) => {
    const { shown, more } = shortNames(list, 12)
    return more > 0 ? `${shown} ${t('revision-olex-more', { count: more })}` : shown
  }
  const rows = OLEX_MATCH_TYPES.map(item => ({
    ...item,
    group: preview.groups.find(g => g.type === item.type),
  })).filter(item => item.group && (item.group.added.length > 0 || item.group.removed.length > 0))
  if (!rows.length) return null

  return (
    <PanelCard>
      <PanelHeader>
        <SectionLabel>{t('revision-olex-changes-title')}</SectionLabel>
      </PanelHeader>
      <Stack>
        {rows.map(({ type, key, group }) => (
          <ListRow key={type}>
            <Typography fontSize={13} sx={{ textTransform: 'capitalize' }}>
              {t(key)}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {group!.added.length > 0 && (
                <Tooltip title={names(group!.added)}>
                  <Tag tone="added">+{group!.added.length}</Tag>
                </Tooltip>
              )}
              {group!.removed.length > 0 && (
                <Tooltip title={names(group!.removed)}>
                  <Tag tone="removed">−{group!.removed.length}</Tag>
                </Tooltip>
              )}
            </Stack>
          </ListRow>
        ))}
      </Stack>
    </PanelCard>
  )
}

export default OlexMatchSummary

const Tag = styled('span', { shouldForwardProp: prop => prop !== 'tone' })<{ tone: 'added' | 'removed' }>(
  ({ theme, tone }) => ({
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'default',
    color: getThemeColor(theme, tone === 'added' ? MUIThemeColors.successDark : MUIThemeColors.errorMain),
    backgroundColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
  }),
)
